/**
 * Drops exact-duplicate material rows (same type/category/description/
 * quantity/unit/gwp/etc.), keeping the first occurrence. Ignores internal
 * tracking fields like `_source` so it also catches duplicates that were
 * saved before that tagging existed (e.g. from repeated "Load from Arivu"
 * clicks). Used both when loading a saved passport (so the cleaned list
 * gets persisted on the next save) and when rendering (Preview/Summary),
 * since already-saved data may still contain duplicates until re-saved.
 */
export const dedupeMaterials = (materials) => {
  if (!Array.isArray(materials)) return materials;
  const seen = new Set();
  return materials.filter((m) => {
    const key = JSON.stringify({
      type: m.type,
      category: m.category,
      description: m.description,
      quantity: m.quantity,
      unit: m.unit,
      gwp: m.gwp,
      biogenic_gwp: m.biogenic_gwp,
      recycledContentPercent: m.recycledContentPercent,
    });
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Attaches a live-computed `material_composition_percentage` to each material
 * — this material's quantity as a share of the total quantity across all
 * materials in the (already deduped) list, times 100. Purely a display value:
 * it's never stored/persisted, so it's always recomputed from whatever the
 * current materials list is, and can't go stale.
 */
export const withMaterialCompositionPercentage = (materials) => {
  if (!Array.isArray(materials)) return materials;
  const total = materials.reduce((s, m) => s + (parseFloat(m.quantity) || 0), 0);
  return materials.map((m) => ({
    ...m,
    material_composition_percentage: total > 0 ? Number(((parseFloat(m.quantity) || 0) / total * 100).toFixed(1)) : 0,
  }));
};
